<template>
    <div class="py-6 px-1 bg-white border border-black" :id="`edit${address.id}`">
        <delivery-address-form
            v-model="deliveryAddress"
            :oldAddress="address"
            :display-plan-dropdown="false"
            @addressChanged="onAddressChanged"
            @postcodeChanged="getCourierName">
        </delivery-address-form>

        <delivery-instructions-field
            v-model="newDeliveryComment"
            :require="true">
        </delivery-instructions-field>

        <delivery-address-property-type-field
            :require="true" v-model="newPropertyType">
        </delivery-address-property-type-field>

        <div class="w-full p-2" v-if="errorHandler.getAllErrors()">
            <ul class="error list-disc text-center">
                <li v-for="(errorMsg, key) in errorHandler.getAllErrors()" :key="key">
                    <span class="font-bold capitalize" v-text="key"></span>:
                    <span class="font-normal" v-text="errorMsg"></span>
                </li>
            </ul>
        </div>
        <div class="w3w-container" v-if="showW3W">
            <what-3-words :value="addressChanged ? null : address.what_three_words"/>
        </div>
        <div class="w-full md:flex pt-4 pb-2 px-2">
            <div class="w-full md:w-1/2 px-0 md:px-1 py-1 md:py-0">
                <button
                    class="w-1/2 btn btn--secondary btn--auto-width btn--full-width"
                    @click="close" v-text="$t('EDIT_BILLING_ADDRESS_FORM_BTN_CANCEL')"></button>
            </div>
            <div class="w-full md:w-1/2 px-0 md:px-1 py-1 md:py-0">
                <submit-button
                    class="btn--full-width"
                    @click="submit"
                    :hoverable="false"
                    :processing="formProcessing" v-text="$t('EDIT_BILLING_ADDRESS_FORM_BTN_UPDATE')"></submit-button>
            </div>
        </div>
    </div>
</template>

<script>
import required from 'vuelidate/src/validators/required';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import AddressService from '@/services/logistics/AddressService';
import SubmitButton from '@/components/forms/buttons/SubmitButton';
import DeliveryInstructionsField from '@/components/forms/fields/DeliveryInstructionsField';
import DeliveryAddressForm from '@/components/forms/addresses/DeliveryAddressForm';
import DeliveryAddressPropertyTypeField from '@/components/forms/fields/LegacyDeliveryAddressPropertyTypeField';
import PostcodeService from '@/services/logistics/PostcodeService';
import What3Words from '@/components/forms/addresses/What3Words';
import { CSPageEvents } from '@/helpers/GAHelpers';

export default {
    name: 'EditDeliveryAddressForm',
    data() {
        return {
            mappedAddress: {
                addressLine1: null,
                addressLine2: null,
                city: null,
                postcode: null,
            },
            courierName: null,
            newDeliveryComment: null,
            newPropertyType: null,
            errorHandler: new ErrorResponseHandler(),
            formProcessing: false,
            addressChanged: false,
        };
    },
    props: {
        address: {
            required: true,
            type: Object,
        },
        propertyType: {
            required: true,
            type: String,
        },
        deliveryComment: {
            required: true,
            type: String,
        },
        subscriptionId: {
            required: true,
            type: Number,
        },
    },
    mounted() {
        this.mapAddressForAddressForm(this.address);
        this.newDeliveryComment = this.deliveryComment;
        this.newPropertyType = this.propertyType;
    },
    methods: {
        onAddressChanged(newAddress) {
            if (
                this.address.address1 === newAddress.addressLine1 &&
                this.address.address2 === newAddress.addressLine2 &&
                this.address.city === newAddress.city &&
                this.address.postcode === newAddress.postcode
            ) return;
            this.addressChanged = true;
        },
        close() {
            this.$emit('closed');
        },
        getCourierName() {
            PostcodeService.GetFirstDeliveryDayForPostcode(this.address.postcode)
                .then(response => this.courierName = response.data.properties.courier_name);
        },
        submit() {
            this.toggleFormProcessing();
            this.validateInput();
        },
        handleUpdatingAddressViaApi() {
            CSPageEvents(`subscription-address-edited`);
            let words = document.getElementById('what3words1')?.value || '';
            let newAddress = {
                firstName: this.address.first_name,
                lastName: this.address.last_name,
                address1: this.mappedAddress.addressLine1,
                address2: this.mappedAddress.addressLine2,
                city: this.mappedAddress.city,
                country: this.address.country,
                postcode: this.mappedAddress.postcode,
                deliveryComment: this.newDeliveryComment,
                propertyType: this.newPropertyType,
                what_3_words: words,
            };
            let service = new AddressService();
            service.updateSubscriptionAddress(this.subscriptionId, this.address.id, newAddress)
                .then(() => {
                    setTimeout(() => {
                        this.$store.dispatch('subscription/fetchAllSubscriptions', true);
                        this.$store.dispatch('address/fetchAllAddresses', true);
                    }, 1000);

                    this.$scrollTo('.main');
                    this.$emit('subscription-address-updated');
                    this.toggleFormProcessing();
                    this.close();
                })
                .catch(error => {
                    this.errorHandler.setResponse(error.response.data);
                    this.toggleFormProcessing();
                });
        },
        mapAddressForAddressForm(address) {
            this.mappedAddress.addressLine1 = address.address1;
            this.mappedAddress.addressLine2 = address.address2;
            this.mappedAddress.city = address.city;
            this.mappedAddress.postcode = address.postcode;
        },
        validateInput() {
            this.$v.$touch();

            this.areAllFormFieldsValid ? this.handleUpdatingAddressViaApi() : this.forceRequiredValidationMessagesVisibility();
        },
        forceRequiredValidationMessagesVisibility() {
            if (this.mappedAddress.address1 === null) this.mappedAddress.address1 = '';
            if (this.mappedAddress.city === null) this.mappedAddress.city = '';
            if (this.mappedAddress.postcode === null) this.mappedAddress.postcode = '';
            if (this.newDeliveryComment === null) this.newDeliveryComment = '';
            if (this.newPropertyType === null) this.newPropertyType = '';

            this.$scrollTo(`#edit${this.address.id}`);
            this.toggleFormProcessing();
        },
        toggleFormProcessing() {
            this.formProcessing = !this.formProcessing;
        },
    },
    computed: {
        areAllFormFieldsValid() {
            return !this.$v.$invalid;
        },
        deliveryAddress: {
            get() {
                return this.mappedAddress;
            },
            set(address) {
                this.mappedAddress.addressLine1 = address.addressLine1;
                this.mappedAddress.addressLine2 = address.addressLine2;
                this.mappedAddress.city = address.city;
                this.mappedAddress.postcode = address.postcode;
            },
        },
        showW3W() {
            return this.courierName?.toLowerCase().includes('global') || this.courierName?.toLowerCase().includes('ff vans') || this.courierName?.toLowerCase().includes('ff bikes');
        },
    },
    validations: {
        mappedAddress: {
            addressLine1: {
                required,
            },
            city: {
                required,
            },
            postcode: {
                required,
            },
        },
        newPropertyType: {
            required,
        },
        newDeliveryComment: {
            required,
        },
    },
    components: {
        DeliveryAddressForm,
        DeliveryInstructionsField,
        DeliveryAddressPropertyTypeField,
        SubmitButton,
        What3Words,
    },
};
</script>

<style lang="scss" scoped>
.w3w-container {
    margin: 0 auto;
    width: 90%;
}
</style>
